import dayjs from 'dayjs'
import { MantineReactTable, MRT_ColumnDef, MRT_PaginationState, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useEffect, useMemo, useState } from 'react'
import { OrderHistory, OrderWithHistory } from 'types/orders'
import { OrderPlace } from 'types/work'
import { getOrderPlaceName } from 'utils'

type Props = {
  data: OrderWithHistory
}

export default function OrderHistoryTable({ data }: Props) {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: Math.min(data.history.length, 10),
  })
  const paginatedData = useMemo(
    () =>
      data?.history.slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize),
    [data, pagination]
  )

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: Math.min(data.history.length, 10),
    })
  }, [data])

  const columns = useMemo<Array<MRT_ColumnDef<OrderHistory>>>(
    () => [
      {
        accessorKey: 'date',
        header: 'Data',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date>()
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
        size: 170,
      },
      {
        accessorKey: 'user.fullName',
        header: 'Użytkownik',
        size: 150,
      },
      {
        accessorKey: 'description',
        header: 'Opis',
        size: 280,
      },
      {
        accessorKey: 'orderPlace',
        header: 'Przekazane do',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: Object.values(OrderPlace).map((place) => ({
            value: place,
            label: getOrderPlaceName(place),
          })),
        },
        Cell: ({ cell }) => {
          const place = cell.getValue<OrderPlace>()
          return getOrderPlaceName(place)
        },
        size: 140,
      },
      {
        accessorKey: 'comments',
        header: 'Komentarz',
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns,
    data: paginatedData || [],
    state: {
      pagination: pagination,
    },
    rowCount: data?.history.length || 0,
    layoutMode: 'grid',
    enableSorting: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: data && data.history.length >= 10 ? true : false,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    onPaginationChange: setPagination,
    manualPagination: true,
  })

  return <MantineReactTable table={table} />
}
